
import { v4 as uuidv4 } from "uuid";
import { ModelBase, ParentedBase } from "@models/UpsertableBase";
import { DataType, SourceBookType } from "@dndModels/Types";
import CommonUtilities from "@utilities/common_utilities";

const common_util = CommonUtilities.getInstance();

export class SourceBook extends ModelBase {
  data_type: DataType = "source_book";
  abbreviation = "";
  is_free = false;
  third_party = false;
  is_public = false;
  is_archived = false;
  source_book_type: SourceBookType = "Source Book";
  sections: SBSection[] = []; // Changing this so that it will have light sb_sections stored in the source_book.
  // The actual content will be loaded from the db separately when it's needed.
  owned = false; // This is set the same time as update free and shared
  shared = false;

  constructor(obj?: any) {
    super(obj);
    if (obj) {
      this.abbreviation = obj.abbreviation ? obj.abbreviation : "";
      this.is_free = obj.is_free ? obj.is_free : (obj.public ? obj.public : false);
      this.third_party = obj.third_party ? obj.third_party : false;
      this.source_book_type = obj.source_book_type ? obj.source_book_type : (obj.homebrew ? "Homebrew" : "Source Book");
      this.is_public = obj.is_public ? obj.is_public : false;
      this.is_archived = obj.is_archived ? obj.is_archived : false;
      if (obj.sections) {
        this.sections = obj.sections ? obj.sections.map((o: any) => new SBSection(o)) : [];
      }
      // if (this.source_book_type === "Unearthed Arcana") {
      //   debugger;
      // }
    }
  }

  get link_abbrev() {
    return common_util.makeStringLinkReady(this.abbreviation);
  }

  get homebrew() {
    return this.source_book_type === "Homebrew";
  }
  get hb() {
    return this.homebrew;
  }

  get ua() {
    return this.source_book_type === "Unearthed Arcana";
  }

  get blocked() {
    return !this.is_public;
  }
  get archived() {
    return this.is_archived;
  }
  get free() {
    return this.is_free;
  }

  get_detail_format(): string {
    let str = ""; // `${this.source_book_type}`;
    // if (this.is_free) {
    //   str += "; Free";
    // }
    // if (this.third_party) {
    //   str += "; 3rd Party";
    // }
    return str.trim();
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    // debugger;
    return {
      ...base_db_obj,
      abbreviation: this.abbreviation,
      is_free: this.is_free,
      third_party: this.third_party,
      source_book_type: this.source_book_type,
      is_public: this.is_public,
      is_archived: this.is_archived,
      sections: this.sections.map(o => o.toLight())
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      abbreviation: this.abbreviation,
      is_free: this.is_free,
      third_party: this.third_party,
      source_book_type: this.source_book_type,
      is_public: this.is_public,
      is_archived: this.is_archived,
    };
  }

  copy(copyMe: SourceBook, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.abbreviation = copyMe.abbreviation;
    this.is_free = copyMe.is_free;
    this.third_party = copyMe.third_party;
    this.is_public = copyMe.is_public;
    this.is_archived = copyMe.is_archived;
    this.source_book_type = copyMe.source_book_type;
    this.sections = [...copyMe.sections];
  }

  clone(drop_ids = false): SourceBook {
    const new_obj = new SourceBook();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}

/**
 * This is for holding the content of a SourceBook.
 * They can have subsections to any level, though it would be rare to go more than 4 levels deep.
 * 
 * Some sections are associated with objects. 
 * I'm going to make it so that links to the sections for them 
 * will automatically be available on things. 
 * These links will open the section in a modal by default, 
 * but right clicking on them will open them in another tab.
 * 
 * I'm also going to have a system for automatically making pseudo-subsections.
 * An example of this would be something like creating a Spell List section. 
 * By designating it as a Spell List section it will automatically pretend that it has subsections
 * for each Spell List in the Source Book, and going to those subsections will automatically show 
 * all the spells on that spell list, with the default formatting 
 * (shows spell school, and whether it's a ritual or takes concentration, may add more).
 * 
 * I can likewise make it so a section can be designated as a Classes section, 
 * which would create subsections for each class in the book, 
 * which would have its table and all its features. 
 * 
 * Likewise, designating a section as a Races section would do each race and subrace in the book. 
 * I can also have Spells, Feats, and Items sections, each of which would generate subsections for 
 * its type of thing, with the relevant data that should be displayed.
 * 
 * I will need to be able to specify extra options on these sections which generate their subsections
 * for specifying formats. For example in SRD 5.1 the Spell Lists are all together, 
 * so they would go in a Spell Lists section, 
 * but in the PHBs and SRD 5.2.1 the spell lists are included in the sections for their classes, 
 * so I need to make an option for the Classes section for whether to include the Spell List.
 * 
 * Note: I'm going to make the spell descriptions automatically include the content of things 
 * like Spell Level, Concentration, area, etc at the top, similar to how is done on the spell 
 * spell descriptions in the books. Some of the spells actually include that stuff in their descriptions,
 * but not all of them. I will need to strip it out of the ones that have it. 
 * Feats and Items will need similar treatments.
 * 
 */

export type SpecialSectionType = "None" | "Races" | "Classes" | "Spell Lists" | "Spell Descriptions" | "Feats" | "Items" | "Creatures" | "Deities";
export const SPECIAL_SECTION_TYPES: SpecialSectionType[] = ["None","Races","Classes","Spell Lists","Spell Descriptions","Feats","Items","Creatures","Deities"];

export const isSpecialSectionType = (str: string): str is SpecialSectionType => {
  return str === "None" || 
    str === "Races" || 
    str === "Classes" || 
    str === "Spell Lists" || 
    str === "Spell Descriptions" ||
    str === "Feats" || 
    str === "Items" ||
    str === "Creatures" ||
    str === "Deities";
}

export class SBSection extends ParentedBase {
  data_type: DataType = "sb_section";
  subsections: SBSection[] = [];
  parent_type: "Source Book" | "SBSection" = "Source Book";
  parent_id: string = "";
  position: number = 0;

  special_type: SpecialSectionType = "None";
  special_attributes: any = {}; // Some special types look for things in here.
  associated_object_type: string = "";
  associated_object_id: string = "";
  single_page: boolean = false; // If this is true then all of its subsections are displayed with it in SBSectionsViewer. Links to subsections will just jump to the anchor tag at the top of the section.
  // subsection_count: number = 0;

  // get subsections_loaded() {
  //   return this.subsections.length === this.subsection_count;
  // }
  
  constructor(obj?: any) {
    super(obj);
    if (obj) {
      if (this.description.length === 0 && obj.content) {
        this.description = obj.content;
      }
      this.subsections = obj.subsections ? obj.subsections.map((o: any) => new SBSection(o)) : [];
      this.parent_type = obj.parent_type ? obj.parent_type : "Source Book";
      this.parent_id = obj.parent_id ? obj.parent_id : "";
      this.position = obj.position ? obj.position : 0;

      this.special_type = obj.special_type ? obj.special_type : "None";
      this.special_attributes = obj.special_attributes ? obj.special_attributes : {};
      this.associated_object_type = obj.associated_object_type ? obj.associated_object_type : "";
      this.associated_object_id = obj.associated_object_id ? obj.associated_object_id : "";
      this.single_page = obj.single_page ? obj.single_page : false;
    }
  }

  _new_true_id: string = "";
  get true_id() {
    if (this._id === "") {
      if (this._new_true_id === "") {
        this._new_true_id = uuidv4().toString();
      }
      return this._new_true_id;
    }
    return this._id;
  }
  set true_id(value: string) {
    this._id = value;
  }

  get content() {
    return this.description;
  }
  set content(value: string) {
    this.description = value;
  }

  toDBObj(): any {
    return {
      ...super.toDBObj(),
      parent_type: this.parent_type,
      parent_id: this.parent_id,
      position: this.position,
      subsections: this.subsections.map(o => o.toLight()),
      special_type: this.special_type,
      special_attributes: this.special_attributes,
      associated_object_type: this.associated_object_type,
      associated_object_id: this.associated_object_id,
      single_page: this.single_page
    };
  }

  toLight(): any {
    return {
      _id: this._id,
      name: this.name,
      parent_type: this.parent_type,
      parent_id: this.parent_id,
      position: this.position,
      subsections: this.subsections.map(o => o.toLight()),
      special_type: this.special_type,
      special_attributes: this.special_attributes,
      associated_object_type: this.associated_object_type,
      associated_object_id: this.associated_object_id,
      single_page: this.single_page
    };
  }

  copy(copyMe: SBSection, drop_ids = false): void {
    super.copy(copyMe, drop_ids);

    this.parent_type = copyMe.parent_type;
    this.parent_id = copyMe.parent_id;
    this.position = copyMe.position;
    // The correct subsection structure is really only stored on the source book, 
    // so what gets to here should already be correct.
    // if (this.subsections.length === copyMe.subsections.length && this.subsections.length > 0) {
    //   for (const subsection of this.subsections) {
    //     const copySubsection = copyMe.subsections.find(o => o._id === subsection._id);
    //     if (copySubsection) {
    //       subsection.copy(copySubsection);
    //     } else {
    //       console.error(`Section not found`);
    //     }
    //   }
    // } else {
    //   this.subsections = [...copyMe.subsections];
    // }

    this.special_type = copyMe.special_type;
    this.special_attributes = {...copyMe.special_attributes};
    this.associated_object_type = copyMe.associated_object_type;
    this.associated_object_id = copyMe.associated_object_id;
    this.single_page = copyMe.single_page;
  }

  clone(drop_ids = false): SBSection {
    const new_obj = new SBSection();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }
}